import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../../../../store/index';
import {
  Button,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  Select,
  Checkbox,
  FormControl,
  FormControlLabel,
  ListItemText,
  Grid,
  InputLabel,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { fetchPost, fetchPut } from '../../../../../helpers/fetching';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(6, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    width: 'auto',
    height: '20vh',
  },
  form: {
    width: '80%',
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.mainDark} inset`,
      borderRadius: 'initial',
    },
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
  },
  loginInfo: {
    textDecoration: 'none',
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
  },
  register: {
    textDecoration: 'none',
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  mediaLinks: {
    marginBottom: theme.spacing(1),
  },
  errorText: {
    textDecoration: 'none',
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    background: theme.palette.accents.dark,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.main,
    transform: 'scale(1.1)',
  },
  title: {
    letterSpacing: '0.2rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  DialogContent: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    overflow: 'hidden',
    overflowY: 'auto',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
  },
}));

const DEFAULT_VALUES = {
  newUsername: '',
  first_name: '',
  last_name: '',
  email: '',
  aliases: '',
  role: 'technician',
  institution_name: '',
  qa_responsible: false,
  statistics_opt_out: false,
  newPassword: '',
  newRepeatPassword: '',
  language: localStorage.getItem('language') || 'en',
};

export default function RegisterDialog(props) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const institutionList = useSelector((state) => state.operators_devices.institution_list);

  const isEditMode = !!props.editUser;

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [selectedInstitutions, setSelectedInstitutions] = useState('');
  const [serverError, setServerError] = useState('');
  const currentRole = localStorage.getItem('role');

  const role = watch('role');

  useEffect(() => {
    if (props.newRow) {
      reset(DEFAULT_VALUES);
      setServerError('');
      setSelectedInstitutions('');
      setShowPassword(false);
      setShowRepeatPassword(false);
    }
  }, [props.newRow, reset]);

  useEffect(() => {
    if (props.editUser) {
      const user = props.editUser;
      const allNames = Array.isArray(institutionList) ? institutionList.map((inst) => inst.institution) : [];
      const parsedInstitutions = user.institution_name
        ? user.institution_name.split(';').filter(Boolean).join(';')
        : allNames.join(';');

      reset({
        newUsername: user.username || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        aliases: user.aliases || '',
        role: user.role || 'technician',
        institution_name: user.institution_name || '',
        qa_responsible: !!user.qa_responsible,
        statistics_opt_out: !!user.statistics_opt_out,
        newPassword: '',
        newRepeatPassword: '',
        language: user.language || localStorage.getItem('language') || 'en',
      });
      setSelectedInstitutions(parsedInstitutions);
      setServerError('');
      setShowPassword(false);
    }
  }, [props.editUser, reset, institutionList]);

  const handleClose = () => {
    props.setNewRow(false);
    if (props.setEditUser) props.setEditUser(null);
    setServerError('');
  };

  const computeInstitutionName = () => {
    const allNames = Array.isArray(institutionList) ? institutionList.map((inst) => inst.institution) : [];
    const currentSelected = selectedInstitutions ? selectedInstitutions.split(';').filter(Boolean) : [];
    const allSelected = allNames.length > 0 && currentSelected.length === allNames.length;
    return allSelected ? '' : selectedInstitutions;
  };

  const onSubmit = async (data) => {
    setServerError('');

    if (isEditMode) {
      const user = props.editUser;
      const updateData = {};

      if (data.first_name !== (user.first_name || '')) updateData.first_name = data.first_name;
      if (data.last_name !== (user.last_name || '')) updateData.last_name = data.last_name;
      if (data.email !== (user.email || '')) updateData.email = data.email;
      if (data.role !== (user.role || '')) updateData.role = data.role;
      if (data.aliases !== (user.aliases || '')) updateData.aliases = data.aliases;
      if (!!data.qa_responsible !== !!user.qa_responsible) updateData.qa_responsible = data.qa_responsible;
      if (!!data.statistics_opt_out !== !!user.statistics_opt_out)
        updateData.statistics_opt_out = data.statistics_opt_out;

      const newLang = data.language || localStorage.getItem('language') || 'en';
      if (newLang !== (user.language || '')) updateData.language = newLang;

      const newInstitutionName = computeInstitutionName();
      if (newInstitutionName !== (user.institution_name || ''))
        updateData.institution_name = newInstitutionName;

      const hasFieldChanges = Object.keys(updateData).length > 0;
      const hasPasswordChange = !!data.newPassword;

      if (!hasFieldChanges && !hasPasswordChange) {
        handleClose();
        return;
      }

      try {
        if (hasFieldChanges) {
          const response = await fetchPut(`user/${user.username}`, updateData);
          if (!response.success) {
            setServerError(t(`api_messages.${response.msg}`));
            return;
          }
        }

        if (hasPasswordChange) {
          const pwResponse = await fetchPut('set_password_admin', {
            username: user.username,
            new: data.newPassword,
          });
          if (!pwResponse.success) {
            setServerError(t(`api_messages.${pwResponse.msg}`));
            return;
          }
        }

        reset();
        dispatch(
          actions.setSnackbar({
            msg: 'UserPanel.snack_user_updated',
            severity: 'success',
          })
        );
        props.setEditUser(null);
      } catch (error) {
        setServerError(t('users.users_error'));
      }
    } else {
      const newUserData = {
        username: data.newUsername,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        role: data.role,
        aliases: data.aliases,
        language: data.language || localStorage.getItem('language') || 'en',
        created_at: new Date().toISOString(),
        qa_responsible: data.qa_responsible,
        statistics_opt_out: data.statistics_opt_out,
        institution_name: computeInstitutionName(),
        password: data.newPassword,
        active: 1,
      };

      try {
        const response = await fetchPost('users', newUserData);
        if (response.success) {
          reset();
          dispatch(
            actions.setSnackbar({
              msg: 'users.user_registered',
              severity: 'success',
            })
          );
          props.setNewRow(false);
        } else {
          setServerError(t(`api_messages.${response.msg}`));
        }
      } catch (error) {
        setServerError(t('users.users_error'));
      }
    }
  };

  return (
    <Dialog open={props.newRow || isEditMode} fullWidth maxWidth='sm' onClose={handleClose}>
      <DialogTitle className={classes.draggableTitle} id='draggable-title'>
        <Typography className={classes.title}>
          {isEditMode ? t('UserPanel.edit') : t('users.new_user')}
        </Typography>
        <IconButton aria-label='close' className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.DialogContent}>
        <form className={classes.form} onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
          {/* Language */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Controller
              name='language'
              control={control}
              render={({ field }) => (
                <TextField
                  variant='outlined'
                  size='small'
                  {...field}
                  select
                  // style={{ width: '250px' }}
                  label={t('ifu_general.select_language')}
                  fullWidth
                  margin='normal'
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                  }}>
                  <MenuItem value='en'>English</MenuItem>
                  <MenuItem value='de'>Deutsch</MenuItem>
                  <MenuItem value='es'>Español</MenuItem>
                  <MenuItem value='fr'>Français</MenuItem>
                  <MenuItem value='lt'>Lithuanian</MenuItem>
                  <MenuItem value='pl'>Polski</MenuItem>
                  <MenuItem value='it'>Italiano</MenuItem>
                </TextField>
              )}
            />
          </div>
          {/* Username */}
          <Controller
            name='newUsername'
            control={control}
            rules={{
              required: t('LoginRegister.all_required'),
              validate: (v) => isEditMode || !v.includes('_') || t('LoginRegister.error_underscore'),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                size='small'
                variant='outlined'
                required
                margin='normal'
                fullWidth
                label={t('LoginRegister.username')}
                autoFocus={!isEditMode}
                disabled={isEditMode}
                error={!!errors.newUsername}
                helperText={errors.newUsername?.message}
                inputProps={{
                  autoComplete: 'new-username',
                  form: { autoComplete: 'off' },
                }}
              />
            )}
          />

          {/* First name / Last name */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name='first_name'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size='small'
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={t('LoginRegister.first_name')}
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name='last_name'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size='small'
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={t('LoginRegister.last_name')}
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Email */}
          <Controller
            name='email'
            control={control}
            rules={{
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('LoginRegister.error_invalid_email'),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                size='small'
                variant='outlined'
                margin='normal'
                fullWidth
                label={t('LoginRegister.email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                inputProps={{
                  autoComplete: 'new-email',
                  form: { autoComplete: 'off' },
                }}
              />
            )}
          />

          {/* Role + technician options */}
          <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
            <Grid item xs={role === 'technician' ? 4 : 12}>
              <Controller
                name='role'
                control={control}
                rules={{ required: t('LoginRegister.all_required') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size='small'
                    margin='normal'
                    select
                    required
                    label={t('LoginRegister.role')}
                    fullWidth
                    variant='outlined'
                    error={!!errors.role}
                    helperText={errors.role?.message}>
                    <MenuItem value='technician'>{t('users.Technician')}</MenuItem>
                    <MenuItem value='radiologist'>{t('users.Radiologist')}</MenuItem>
                    {currentRole === 'admin' && (
                      <MenuItem value='supervisor'>{t('users.Supervisor')}</MenuItem>
                    )}
                    {currentRole === 'admin' && <MenuItem value='service'>{t('users.Service')}</MenuItem>}
                  </TextField>
                )}
              />
            </Grid>

            {/* QA Responsible */}
            {role === 'technician' && (
              <Grid
                item
                xs={4}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Controller
                  name='qa_responsible'
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} color='primary' />}
                      label={t('LoginRegister.qa_responsible')}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Statistics opt out */}
            {role === 'technician' && (
              <Grid
                item
                xs={4}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Controller
                  name='statistics_opt_out'
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} color='primary' />}
                      label={t('LoginRegister.statistics_opt_out')}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {/* Aliases (technician only) */}
          {role === 'technician' && (
            <Controller
              name='aliases'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size='small'
                  variant='outlined'
                  margin='normal'
                  fullWidth
                  autoComplete='off'
                  label={t('LoginRegister.aliases')}
                  error={!!errors.aliases}
                  helperText={errors.aliases?.message || t('users.helper_alias')}
                  FormHelperTextProps={{
                    classes: { root: classes.helperText },
                  }}
                />
              )}
            />
          )}

          {/* Institutions (radiologist only) */}
          {role === 'radiologist' && (
            <FormControl variant='outlined' style={{ width: '100%', marginTop: '10px' }}>
              <InputLabel id='institutions-label'>{t('users.institutions')}</InputLabel>

              <Select
                labelId='institutions-label'
                id='institutions-select'
                multiple
                displayEmpty
                label={t('users.institutions')}
                value={
                  selectedInstitutions
                    ? selectedInstitutions
                        .split(';')
                        .map((item) => item.trim())
                        .filter(Boolean)
                    : []
                }
                onChange={(event) => {
                  const value = event.target.value.filter(Boolean);

                  if (value.includes('__all__')) {
                    const allNames = institutionList.map((inst) => inst.institution);
                    const currentSelected = selectedInstitutions
                      ? selectedInstitutions.split(';').filter(Boolean)
                      : [];

                    const allSelected = currentSelected.length === allNames.length;
                    setSelectedInstitutions(allSelected ? '' : allNames.join(';'));
                  } else {
                    setSelectedInstitutions(value.join(';'));
                  }
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ opacity: 0.6 }}>{t('users.institutions')}</span>;
                  }
                  return selected.join('; ');
                }}>
                {Array.isArray(institutionList) && institutionList.length > 0 && (
                  <MenuItem value='__all__'>
                    <Checkbox
                      checked={
                        selectedInstitutions?.split(';').filter(Boolean).length === institutionList.length
                      }
                      indeterminate={
                        selectedInstitutions?.split(';').filter(Boolean).length > 0 &&
                        selectedInstitutions?.split(';').filter(Boolean).length < institutionList.length
                      }
                    />
                    <ListItemText primary={t('users.select_all')} />
                  </MenuItem>
                )}

                {Array.isArray(institutionList) &&
                  institutionList.map((inst, index) => (
                    <MenuItem key={`${inst.institution}-${index}`} value={inst.institution}>
                      <Checkbox
                        checked={selectedInstitutions
                          ?.split(';')
                          .map((item) => item.trim())
                          .filter(Boolean)
                          .includes(inst.institution)}
                      />
                      <ListItemText primary={inst.institution} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Password */}
          <Controller
            name='newPassword'
            control={control}
            rules={
              isEditMode
                ? { minLength: { value: 8, message: t('api_messages.password_too_short') } }
                : {
                    required: t('LoginRegister.all_required'),
                    minLength: { value: 8, message: t('api_messages.password_too_short') },
                  }
            }
            render={({ field }) => (
              <TextField
                {...field}
                size='small'
                variant='outlined'
                margin='normal'
                fullWidth
                required={!isEditMode}
                label={isEditMode ? t('UserPanel.new') : t('LoginRegister.password')}
                type={showPassword ? 'text' : 'password'}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message || (isEditMode ? t('UserPanel.password_helper') : '')}
                inputProps={{
                  autoComplete: 'new-password',
                  form: { autoComplete: 'off' },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge='end' size='small'>
                        {showPassword ? (
                          <VisibilityOffIcon fontSize='small' />
                        ) : (
                          <VisibilityIcon fontSize='small' />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Repeat password - only for new user */}
          {!isEditMode && (
            <Controller
              name='newRepeatPassword'
              control={control}
              rules={{
                required: t('LoginRegister.all_required'),
                validate: (v) => v === getValues('newPassword') || t('LoginRegister.not_match'),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size='small'
                  variant='outlined'
                  margin='normal'
                  fullWidth
                  required
                  label={t('LoginRegister.repeat')}
                  type={showRepeatPassword ? 'text' : 'password'}
                  error={!!errors.newRepeatPassword}
                  helperText={errors.newRepeatPassword?.message}
                  inputProps={{
                    autoComplete: 'new-password-repeat',
                    form: { autoComplete: 'off' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => setShowRepeatPassword((prev) => !prev)}
                          edge='end'
                          size='small'>
                          {showRepeatPassword ? (
                            <VisibilityOffIcon fontSize='small' />
                          ) : (
                            <VisibilityIcon fontSize='small' />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          )}

          <div className={classes.errorContainer}>
            {serverError && (
              <Chip
                icon={<ErrorOutlineIcon />}
                label={serverError}
                onDelete={() => setServerError('')}
                color='secondary'
                variant='default'
                className={classes.errorText}
              />
            )}
            <Button
              fullWidth
              variant='contained'
              color='primary'
              className={classes.submit}
              type='submit'
              size='large'
              disabled={isSubmitting}>
              {isEditMode ? t('ResultView.btn_save') : t('users.add_user')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
